import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { useAuth } from "@/store/auth";

const baseURL = (import.meta.env.VITE_API_BASE_URL as string) || "";

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// ---------------- Request interceptor: attach Bearer + Branch ----------------
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken, user, activeBranchId } = useAuth.getState();
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  const effectiveBranchId = activeBranchId ?? user?.branchId;
  if (effectiveBranchId) config.headers["X-Branch-Id"] = effectiveBranchId;
  // Generate correlation id per request
  if (!config.headers["X-Correlation-Id"]) {
    config.headers["X-Correlation-Id"] = crypto.randomUUID();
  }
  return config;
});

// ---------------- Response interceptor: error normalization ----------------
export interface ApiProblem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  correlationId?: string;
  errors?: Record<string, string[]> | object;
  instance?: string;
}

api.interceptors.response.use(
  (resp) => resp,
  (err: AxiosError<ApiProblem>) => {
    const status = err.response?.status;

    // 401 -> clear session. Redirect is handled *inside* the SPA by AuthGuard
    // (which re-renders to /login when isAuthenticated() becomes false). We must NOT do a
    // full-page location.assign here: on the shared host that reloads the legacy app's /login.
    if (status === 401) {
      const auth = useAuth.getState();
      const wasAuthed = !!auth.accessToken;
      auth.clear();
      if (wasAuthed && !location.pathname.startsWith("/login")) {
        auth.setSessionExpired(true);
      }
    }

    return Promise.reject(err);
  }
);

// Helper to extract a user-facing message from any error
export function errorMessage(err: unknown): string {
  if (axios.isAxiosError<ApiProblem>(err)) {
    const data = err.response?.data;
    if (data?.title) return data.title;
    if (data?.detail) return data.detail;
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return "An unexpected error occurred";
}
